import { maybe } from '@passionware/monads';
import type { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { parseDate as parseCalendarDate } from '@internationalized/date';
import type { Resource } from '__types__/v1/models';
import { format } from 'date-fns';
import { rateAmountFromBookingWire } from 'v5/api/booking/bookingRateAmount.http';
import type { BookingStatusSnapshot } from 'v5/api/booking/pickInitialBookingStatusFromV1.http';
import { parseBookingRateInterval } from 'v5/api/resourcing/resourcingInterval.http';
import type { CurrencyCode } from 'v5/core/money/model';
import type { CreateResourceAssignmentResourceInput } from 'v5/core/production-resourcing/mutation-inputs';
import type { SlotPlannedDisplayLabels } from 'v5/features/productions/resourcing/calculation/resourcingDisplay';
import { resourceRow } from 'v5/patterns/resource-picker/resourceRow';
import type { WithServices } from 'v5/platform/ts/services';
import type { UserFlowService } from 'v5/services/front/UserFlowService/UserFlowService';
import type { UserPreferencesService } from 'v5/services/front/UserPreferencesService/UserPreferencesService';
import type { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import type { WithProductionTypeService } from 'v5/services/internal/ProductionTypeService/ProductionTypeService';
import type { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import type { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import type { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import type { WithGroupsService } from 'v5/services/io/GroupsService/GroupsService';
import type { WithProductionService } from 'v5/services/io/ProductionService/ProductionService';
import type { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import type { WithTagService } from 'v5/services/io/TagsService/TagsService';
import { formatRateSummary } from 'v1/helpers/budgetHelper';

/**
 * Minimal slot/plan summary the dialog needs for the review step (ordinals, labels,
 * planned estimate badges). Maps cleanly from resourcing slots or any similar model.
 */
export type AssignResourceSlotSummary = {
  id: number;
  productionId: number;
  slotCategoryId: number | null;
  slotCategoryName?: string | null;
  groupDisplayOrdinal?: number | null;
  slotDisplayOrdinal?: number | null;
} & Partial<SlotPlannedDisplayLabels>;

export interface ResourceAssignmentCandidate
  extends CreateResourceAssignmentResourceInput {
  role?: string;
  defaultRateSummary?: string | null;
  firstConflictStartDate?: string | null;
  conflictsCount?: number;
  avatarUrl?: string | null;
  initials?: string;
}

export type AssignResourceDialogServices = WithServices<
  [
    WithResourceService,
    WithResourceTypeService,
    WithTagService,
    WithGroupsService,
    WithProductionTypeService,
    WithMetastructureMappingService,
    WithAppScopeService,
    WithEnterpriseService,
    WithProductionService,
    WithFeatureService
  ]
>['services'] & {
  userPreferencesService?: UserPreferencesService;
  userFlowService?: UserFlowService;
};

export interface AssignResourceDialogProps {
  /** Target row/slot the user is assigning into (production id drives Redux lookups). */
  slot: AssignResourceSlotSummary;
  services: AssignResourceDialogServices;
  onAssignResources: (
    resources: ResourceAssignmentCandidate[]
  ) => Promise<void> | void;
  /** Controlled open state (always controlled — render your own opener or use mount-element). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Resource ids to pre-select in the directory (e.g. everyone already on this production).
   * Matching rows are selected as they appear in the list; those ids cannot be “added” — they are omitted
   * from the review list and from `onAssignResources`. Also pass `slotOccupantResourceIds` so people
   * already on this slot are not duplicated on the API.
   */
  selectedResourceIds?: readonly number[];
  /**
   * Resource ids already on the target slot: omitted from `onAssignResources` so bookings are not
   * duplicated. Optional when the caller has no slot view.
   */
  slotOccupantResourceIds?: readonly number[];
}

function toCandidate(resource: {
  id: number;
  full_name?: string;
  first_name?: string;
  last_name?: string;
}): ResourceAssignmentCandidate {
  return {
    id: resource.id,
    fullName:
      resource.full_name ||
      [resource.first_name, resource.last_name].filter(Boolean).join(' ') ||
      'Unnamed resource'
  };
}

export function parseDate(value: unknown): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function overlaps(
  left: { start: Date; end: Date },
  right: { start: Date; end: Date }
): boolean {
  return left.start <= right.end && right.start <= left.end;
}

export type BuildAssignmentCandidateContext = {
  productionDate: {
    start_date?: string;
    end_date?: string;
    all_day_duration_hours?: number | null;
    include_weekends?: boolean | null;
  } | null;
  productionRange: { start: Date; end: Date } | null;
  bookingDateRangeLabel: string | null;
  confirmedStatus: BookingStatusSnapshot | null;
};

/** The resource's live bookings in the production dates; all of them while the dates are unknown. */
export function conflictingBookingsOf(
  resource: Resource,
  productionRange: { start: Date; end: Date } | null
) {
  const activeBookings = (resource.bookings ?? []).filter(
    booking => !booking.archived && !booking.cancelled_at
  );
  return productionRange
    ? activeBookings.filter(booking => {
        const start = parseDate(booking.start_date);
        const end = parseDate(booking.end_date ?? booking.start_date);
        if (!start || !end) {
          return false;
        }
        return overlaps(productionRange, {
          start,
          end
        });
      })
    : activeBookings;
}

/** The earliest start among the bookings, if any has a date. */
export function firstConflictStartOf(
  bookings: ReturnType<typeof conflictingBookingsOf>
): Date | undefined {
  return bookings
    .map(booking => parseDate(booking.start_date))
    .filter((date): date is Date => date != null)
    .sort((a, b) => a.getTime() - b.getTime())[0];
}

export const conflictDateLabel = (date: Date) => format(date, 'dd/MM/yyyy');

export function buildResourceAssignmentCandidate(
  resource: Resource,
  ctx: BuildAssignmentCandidateContext
): ResourceAssignmentCandidate {
  const candidateBase = toCandidate(resource);
  const rate = resourceRow.firstRate(resource);
  const defaultRateSummary = rate
    ? formatRateSummary(
        {
          amount: rate.amount,
          currency: rate.currency,
          interval: rate.interval,
          quantity: 1
        },
        rate.currency || 'USD',
        false
      )
    : null;
  const conflictingBookings = conflictingBookingsOf(
    resource,
    ctx.productionRange
  );
  const firstConflictStart = firstConflictStartOf(conflictingBookings);
  const productionDate = ctx.productionDate;
  return {
    ...candidateBase,
    role: resource.role?.trim() || undefined,
    defaultRateSummary,
    optimisticBookingDetails: ctx.bookingDateRangeLabel,
    optimisticSchedules:
      productionDate?.start_date && productionDate?.end_date
        ? [
            {
              kind: 'recurring',
              range: {
                from: parseCalendarDate(productionDate.start_date),
                to: parseCalendarDate(productionDate.end_date),
                includeWeekends: productionDate.include_weekends ?? false
              },
              dailyHours: {
                kind: 'allDay',
                hoursPerDay: productionDate.all_day_duration_hours ?? 8
              }
            }
          ]
        : null,
    optimisticRate: rate
      ? {
          amountPerUnit:
            rate.amount != null
              ? (rateAmountFromBookingWire(rate.amount) ?? undefined)
              : undefined,
          currency: (rate.currency ?? undefined) as CurrencyCode | undefined,
          interval: parseBookingRateInterval(rate.interval),
          fees: maybe.of({
            agency: rate.agency_fee ?? 0,
            payroll: rate.payroll_fee ?? 0,
            processing: rate.processing_fee ?? 0
          })
        }
      : null,
    optimisticStatusId: ctx.confirmedStatus?.id ?? null,
    optimisticStatusName: ctx.confirmedStatus?.name ?? 'Confirmed',
    optimisticStatusColor: ctx.confirmedStatus?.colour ?? null,
    firstConflictStartDate: firstConflictStart
      ? conflictDateLabel(firstConflictStart)
      : null,
    conflictsCount: conflictingBookings.length,
    avatarUrl: resource.profile_picture ?? null,
    initials: resourceRow.initials(resource)
  };
}
